import { SEO } from '../components/common/SEO';
import { Button } from '../components/ui/Button';
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import type { UpsertReferralRequest } from '../types';

// Configuration
const CONFIG = {
    VALIDATION_DEBOUNCE: 500,
    MIN_REFERRAL_LENGTH: 4,
    MAX_REFERRAL_LENGTH: 20
};

// Types
interface FormData {
    email: string;
    referralCode: string;
}

interface ValidationState {
    email: {
        isValid: boolean;
        error: string;
        success: string;
    };
    referralCode: {
        isValid: boolean;
        error: string;
        success: string;
        status: string;
    };
}

export const ReferralPage: React.FC = () => {
    const [formData, setFormData] = useState<FormData>({
        email: '',
        referralCode: '',
    });

    const [validation, setValidation] = useState<ValidationState>({
        email: { isValid: false, error: '', success: '' },
        referralCode: { isValid: false, error: '', success: '', status: '' }
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [referralCodeFromUrl, setReferralCodeFromUrl] = useState('');
    const [showIosNotice, setShowIosNotice] = useState(false);

    const [apiFeedback, setApiFeedback] = useState<{ type: 'success' | 'error', message: string } | null>(null);

    const showIosComingSoon = () => {
        setShowIosNotice(true);
        window.setTimeout(() => setShowIosNotice(false), 3200);
    };

    // Clear feedback when user types
    useEffect(() => {
        if (apiFeedback) {
            setApiFeedback(null);
        }
    }, [formData]);

    const validateEmailFormat = (email: string): boolean => {
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        return emailRegex.test(email) && email.length <= 254;
    };

    const validateReferralFormat = (code: string): boolean => {
        if (!code) return false; // Now required
        const codeRegex = /^[A-Z0-9]+$/i;
        return code.length >= CONFIG.MIN_REFERRAL_LENGTH &&
            code.length <= CONFIG.MAX_REFERRAL_LENGTH &&
            codeRegex.test(code);
    };

    // Remove mock — validation is format-only; the real API handles code existence checks

    // Real API registration using upsertReferral
    const submitReferralRegistration = async (
        referralCode: string,
        email: string
    ) => {
        try {
            // Prepare the request payload matching Swagger spec
            const payload: UpsertReferralRequest = {
                referralCode: referralCode.trim(),
                toEmailId: email.trim().toLowerCase()
            };

            // Call the API service
            return await apiService.upsertReferral(payload);
        } catch (error) {
            console.error('Referral registration error:', error);
            // Return a safe failure response if the call itself crashes (network exception not caught inside apiService)
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
                message: 'Registration failed',
                status: false,
                data: null
            };
        }
    };

    // Validation functions
    const validateEmail = useCallback(() => {
        const email = formData.email.trim();

        if (!email) {
            setValidation(prev => ({
                ...prev,
                email: { isValid: false, error: 'Email address is required', success: '' }
            }));
            return false;
        }

        if (!validateEmailFormat(email)) {
            setValidation(prev => ({
                ...prev,
                email: {
                    isValid: false,
                    error: 'Please enter a valid email address',
                    success: ''
                }
            }));
            return false;
        }

        setValidation(prev => ({
            ...prev,
            email: {
                isValid: true,
                error: '',
                success: '✓ Valid email address'
            }
        }));
        return true;
    }, [formData.email]);

    const validateReferralCode = useCallback((code: string) => {
        if (!code) {
            setValidation(prev => ({
                ...prev,
                referralCode: {
                    isValid: false,
                    error: 'Referral code is required',
                    success: '',
                    status: ''
                }
            }));
            return false;
        }

        if (!validateReferralFormat(code)) {
            setValidation(prev => ({
                ...prev,
                referralCode: {
                    isValid: false,
                    error: 'Referral code must be 4-20 characters, letters and numbers only',
                    success: '',
                    status: '✗ Invalid'
                }
            }));
            return false;
        }

        // Format is valid — actual code existence is checked by the upsertReferral API on submit
        setValidation(prev => ({
            ...prev,
            referralCode: {
                isValid: true,
                error: '',
                success: '✓ Valid referral code format',
                status: '✓ Valid'
            }
        }));
        return true;
    }, []);

    // Get referral code from URL
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const refCode = urlParams.get('ref') ||
            urlParams.get('referral') ||
            urlParams.get('code') ||
            urlParams.get('invite') || '';

        if (refCode) {
            setReferralCodeFromUrl(refCode);
            setFormData(prev => ({ ...prev, referralCode: refCode }));
            validateReferralCode(refCode);
        }
    }, [validateReferralCode]);

    // Handle input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Trigger validation based on field
        if (name === 'referralCode') {
            validateReferralCode(value.trim());
        }
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isSubmitting) return;

        // Clear previous feedback
        setApiFeedback(null);

        const email = formData.email.trim();
        const referralCode = formData.referralCode.trim();

        // Validate all fields — both are required
        const emailValid = validateEmail();
        const referralValid = validateReferralCode(referralCode);

        if (!emailValid || !referralValid) {
            return;
        }

        // Start submission
        setIsSubmitting(true);

        try {
            // Call the real API endpoint
            const response = await submitReferralRegistration(referralCode, email);

            // Check if HTTP request was successful and we have data
            if (response.success && response.data) {
                // Check backend logic status
                if (response.data.status === true) {
                    setIsSubmitted(true);

                    // Track successful registration
                    trackEvent('referral_registration_successful', {
                        email: email,
                        referralCode: referralCode,
                        source: 'referral-page',
                        timestamp: new Date().toISOString()
                    });

                    console.log('✅ Referral registration successful:', response.data.message);
                } else {
                    // Backend logic failure (false)
                    const message = response.data.message || 'Registration failed';

                    setApiFeedback({
                        type: 'error',
                        message: message
                    });
                }
            } else {
                // Network or Server Error (HTTP non-200)
                const errorMsg = response.message || response.error || 'An unexpected error occurred';
                setApiFeedback({
                    type: 'error',
                    message: errorMsg
                });
            }

        } catch (error: unknown) {
            console.error('❌ Referral registration unexpected error:', error);
            const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
            setApiFeedback({
                type: 'error',
                message: errorMessage
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Analytics tracking
    const trackEvent = (eventName: string, data: Record<string, unknown> = {}) => {
        try {
            console.log('Event tracked:', eventName, data);
        } catch (error) {
            console.error('Tracking error:', error);
        }
    };

    // Download link handlers
    const handleDownloadClick = (platform: string) => {
        trackEvent('app_download_click', { platform });
        if (platform === 'ios') {
            showIosComingSoon();
        }
    };

    return (
        <div>
            {showIosNotice && (
                <div
                    role="status"
                    aria-live="polite"
                    className="fixed bottom-6 left-1/2 z-[60] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 rounded-xl bg-gray-950 px-5 py-4 text-center text-sm font-semibold text-white shadow-2xl"
                >
                    iOS app is coming soon. Android is available now.
                </div>
            )}
            <SEO
                title="Join Swag Shots via Referral - Get Started Today"
                description="Join Swag Shots through a friend's referral. Register with your email and download the Android app after registration. iOS is coming soon."
                url="https://www.swagshots.com/referral-register"
            />

            <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full">
                    {!isSubmitted ? (
                        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                            {/* Header */}
                            <div className="bg-gradient-to-br from-primary-600 to-primary-500 text-white px-6 py-8 text-center">
                                <h1 className="text-2xl font-bold mb-2">🎉 Join Swag Shots!</h1>
                                <p className="text-primary-100">
                                    You've been invited to join the amazing Swag Shots community. Enter your details below to get started!
                                </p>
                            </div>

                            {/* Form */}
                            <div className="px-6 py-8">
                                <form onSubmit={handleSubmit} className="space-y-6" noValidate>

                                    {/* API Feedback Message */}
                                    {apiFeedback && (
                                        <div className={`p-4 rounded-lg mb-4 text-sm font-medium ${apiFeedback.type === 'success'
                                                ? 'bg-green-50 text-green-800 border border-green-200'
                                                : 'bg-red-50 text-red-800 border border-red-200'
                                            }`}>
                                            <div className="flex items-center gap-2">
                                                <span>{apiFeedback.type === 'success' ? '✅' : '⚠️'}</span>
                                                <p>{apiFeedback.message}</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Email Field */}
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                                            Email Address *
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            required
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            onBlur={validateEmail}
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${validation.email.error ? 'border-red-500 bg-red-50' :
                                                validation.email.success ? 'border-green-500 bg-green-50' :
                                                    'border-gray-300'
                                                }`}
                                            placeholder="Enter your email address"
                                            maxLength={254}
                                            autoComplete="email"
                                        />
                                        {validation.email.error && (
                                            <p className="text-red-500 text-xs mt-1 font-medium">
                                                {validation.email.error}
                                            </p>
                                        )}
                                        {validation.email.success && (
                                            <p className="text-green-600 text-xs mt-1 font-medium">
                                                {validation.email.success}
                                            </p>
                                        )}
                                    </div>

                                    {/* Referral Code Field */}
                                    <div className="relative">
                                        <label htmlFor="referralCode" className="block text-sm font-semibold text-gray-700 mb-2">
                                            Referral Code *
                                        </label>
                                        <input
                                            type="text"
                                            id="referralCode"
                                            name="referralCode"
                                            required
                                            value={formData.referralCode}
                                            onChange={handleInputChange}
                                            onBlur={(e) => {
                                                const code = e.target.value.trim();
                                                validateReferralCode(code);
                                            }}
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${validation.referralCode.error ? 'border-red-500 bg-red-50' :
                                                validation.referralCode.success ? 'border-green-500 bg-green-50' :
                                                    'border-gray-300'
                                                } ${referralCodeFromUrl ? 'bg-gray-50' : ''}`}
                                            placeholder="Enter referral code"
                                            maxLength={20}
                                            autoComplete="off"
                                            readOnly={!!referralCodeFromUrl}
                                        />
                                        {validation.referralCode.status && (
                                            <div className={`absolute right-3 top-10 text-xs font-semibold ${validation.referralCode.status.includes('✓') ? 'text-green-600' : 'text-red-500'
                                                }`}>
                                                {validation.referralCode.status}
                                            </div>
                                        )}
                                        {validation.referralCode.error && (
                                            <p className="text-red-500 text-xs mt-1 font-medium">
                                                {validation.referralCode.error}
                                            </p>
                                        )}
                                        {validation.referralCode.success && (
                                            <p className="text-green-600 text-xs mt-1 font-medium">
                                                {validation.referralCode.success}
                                            </p>
                                        )}
                                        {referralCodeFromUrl && (
                                            <p className="text-primary-600 text-xs mt-1">
                                                ✅ Referral code from your invitation link
                                            </p>
                                        )}
                                        {!referralCodeFromUrl && !validation.referralCode.error && !validation.referralCode.success && (
                                            <p className="text-gray-500 text-xs mt-1">
                                                Required: Referral code from your friend's invitation
                                            </p>
                                        )}
                                    </div>

                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="w-full py-3 text-lg flex items-center justify-center gap-2"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                Processing...
                                            </>
                                        ) : (
                                            'Register & Download App'
                                        )}
                                    </Button>

                                    <p className="text-xs text-gray-500 text-center">
                                        By registering, you agree to our{' '}
                                        <Link to="/terms" className="text-primary-600 hover:text-primary-700">
                                            Terms of Use
                                        </Link>{' '}
                                        and{' '}
                                        <Link to="/privacy-policy" className="text-primary-600 hover:text-primary-700">
                                            Privacy Policy
                                        </Link>
                                    </p>
                                </form>
                            </div>
                        </div>
                    ) : (
                        // Success State
                        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                            <div className="px-6 py-8 text-center">
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                                    <p className="text-green-800 font-semibold mb-2">✅ Registration successful! Welcome to the Swag Shots family!</p>
                                </div>

                                <h1 className="text-2xl font-bold text-gray-900 mb-4">🚀 Download Swag Shots Now!</h1>
                                <p className="text-gray-600 mb-8">
                                    Android is available now. iOS is coming soon.
                                </p>

                                <div className="space-y-4">
                                    <div className="flex flex-col space-y-3">
                                        <a
                                            href="https://play.google.com/store/apps/details?id=com.swagshots.app"
                                            onClick={() => handleDownloadClick('android')}
                                            className="inline-flex items-center justify-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                                        >
                                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993.0001.5511-.4482.9997-.9993.9997zm-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993 0 .5511-.4482.9997-.9993.9997zm11.4045-6.02l1.9973-3.4592c.2204-.3813.0906-.8738-.2907-1.0942-.3813-.2204-.8738-.0906-1.0942.2907l-2.0267 3.5084c-1.6675-.7586-3.5351-1.1825-5.5335-1.1825-1.9984 0-3.866.4239-5.5335 1.1825l-2.0267-3.5084c-.2204-.3813-.7129-.5111-1.0942-.2907-.3813.2204-.5111.7129-.2907 1.0942l1.9973 3.4592c-2.9424 1.6192-4.9326 4.7667-4.9326 8.3616h20c0-3.5949-1.9902-6.7424-4.9326-8.3616z" />
                                            </svg>
                                            Android
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => handleDownloadClick('ios')}
                                            className="inline-flex items-center justify-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                                        >
                                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                                            </svg>
                                            iOS Coming Soon
                                        </button>
                                        <Link to="/">
                                            <Button variant="secondary" className="w-full">
                                                Back to Home
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Bottom Links */}
                    {/* <div className="text-center mt-6">
                        <p className="text-gray-500 text-sm">
                            Already have an account?{' '}
                            <Link to="/#download" className="text-primary-600 hover:text-primary-700 font-semibold">
                                Download App
                            </Link>
                        </p>
                    </div> */}
                </div>
            </div>
        </div>
    );
};
